/*
 * ******************************************************************
 * Copyright (c) 2023-2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { Injectable } from "@angular/core";
import { L10nService } from "@vmw/ngx-vip";
import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { AppConfigService } from "@app/services/app-config/app-config.service";
import { ConsoleType } from "@shared/models/global.model";
import { CommonData, CommonErrorDef, TaskV2 } from "@api/generated/vcdr";

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class ErrorService {

    private static helpInfoCodes: Array<string> = ["VCDS-SP-001-00054"];
    private static featureRestrictedCodes: Array<string> = ["VCDS-SP-001-00007"];

    constructor(private l10nService: L10nService,
                private appConfig: AppConfigService) {
    }

    public getUiErrorMessage(error?: HttpErrorResponse,
                             unknownErrorResource?: string,
                             httpErrorResource?: string): string {
        let message: string = "";
        if (error?.error?.error?.errormessage) {
            message = error.error.error.errormessage;
            if (error.error.error.errorcode) {
                message += " (" + error.error.error.errorcode + ")";
            }
        } else if (error && error.status === 0) {
            const serviceName = this.l10nService.getMessage(this.appConfig.getServiceName());
            message = this.l10nService.getMessage(
                "console.backend.unreachable.error", [serviceName]);
        } else if (error && error.status === HttpStatusCode.Forbidden) {
            const serviceName = this.l10nService.getMessage(this.appConfig.getServiceName());
            message = this.l10nService.getMessage(
                "console.no.access.error", [serviceName]);
        } else {
            if (error && error.status > 0 && error.statusText && httpErrorResource) {
                message = this.l10nService.getMessage(
                    httpErrorResource, [error.status, error.statusText]);
            } else if (unknownErrorResource) {
                message = this.l10nService.getMessage(unknownErrorResource);
            } else if (this.appConfig.clientType === ConsoleType.OPERATOR && error?.message) {
                message = error.message;
            }
            if (error) {
                console.warn("Missing error code for error:", error);
            }
        }
        return message;
    }

    public getTaskErrorMessage(task?: TaskV2,
                               unknownErrorResource?: string,
                               httpErrorResource?: string): string {
        const data: CommonData = task.data;
        const errorDef: CommonErrorDef = data.errors.length > 0 ? data.errors[0] : null;
        let message = errorDef?.error_message;
        if (errorDef?.error_code) {
            message = message ? `${message} (${errorDef.error_code})` : errorDef.error_code;
        }
        message = message || this.getUiErrorMessage(null, unknownErrorResource, httpErrorResource);
        return message;
    }

    public getConsoleErrorMessage(error?: HttpErrorResponse,
                                  unknownErrorResource?: string,
                                  httpErrorResource?: string): string {
        let message: string = "";
        if (error?.error?.error?.errormessage) {
            message = error.error.error.errormessage;
        } else if (this.appConfig.clientType === ConsoleType.OPERATOR && error?.message) {
            message = error.message;
        } else if (error && error.status > 0 && error.statusText && httpErrorResource) {
            message = this.l10nService.getMessage(
                httpErrorResource, [error.status, error.statusText]);
        } else if (unknownErrorResource) {
            message = this.l10nService.getMessage(unknownErrorResource);
        }
        return message;
    }

    public getHelpInfoUrl(error?: HttpErrorResponse): string {
        let url: string = "";
        if (error?.error?.error?.help_info && error.error.error.help_info.length > 0) {
            url = error.error.error.help_info[0];
        }
        return url;
    }

    public getHelpInfo(error?: HttpErrorResponse): string {
        let url: string = "";
        if (error?.error?.error?.help_info && error.error.error.help_info.length > 0) {
            url = error.error.error.help_info[0];
        }
        return url;
    }

    public needHelpInfo(error?: HttpErrorResponse): boolean {
        const errorCode = error?.error?.error?.errorcode;
        return ErrorService.helpInfoCodes.includes(errorCode);
    }

    public isFeatureRestricted(error?: HttpErrorResponse): boolean {
        const errorCode = error?.error?.error?.errorcode;
        return ErrorService.featureRestrictedCodes.includes(errorCode);
    }
}
